import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '../../ui/organisms/Dialog';
import { Button } from '../../ui/atoms/Button';
import { Card, Typography } from '../../ui/atoms';
import { Candidate } from '../../../models/job';
import { EmailField } from '../../ui/molecules';
import { isValidEmail } from '../../../utils/candidate-email';
import { SendNotificationCheckbox } from '../candidate-actions/SendNotificationCheckbox';
import { CandidateIdentity } from '../candidate-actions/CandidateIdentity';
import { getGTMAttributes } from '../../../utils/gtm/gtm-tracking';
import {
  getConfirmationDialogButtonTracking,
  getConfirmationDialogCloseTracking,
} from '../../../utils/gtm/gtm-confirmation-dialog';

export interface SendInterviewConfirmationDialogProps {
  open: boolean;
  candidateIds: string[];
  candidates: Candidate[];
  onClose: () => void;
  onConfirm: () => void;
  isPending?: boolean;
  origin?: string;
  /** For single send when candidate has no email: controlled value for inline email input */
  missingEmailValue?: string;
  /** Called when user types in the inline email field (single send, missing email) */
  onMissingEmailChange?: (value: string) => void;
  /** When false, copy must not promise an invitation email. Defaults to true. */
  willNotify?: boolean;
  /** Per-move opt-out (single send only). The checkbox renders only when this handler is set. */
  sendNotification?: boolean;
  onSendNotificationChange?: (value: boolean) => void;
}

/**
 * Send interview confirmation: bulk shows count only; single can show name or inline email when missing.
 * Confirm disabled until valid email when in "missing email" mode.
 */
export const SendInterviewConfirmationDialog: React.FC<SendInterviewConfirmationDialogProps> = ({
  open,
  candidateIds,
  candidates,
  onClose,
  onConfirm,
  isPending = false,
  origin = 'candidate_actions',
  missingEmailValue = '',
  onMissingEmailChange,
  willNotify: stageWillNotify = true,
  sendNotification = true,
  onSendNotificationChange,
}) => {
  const { t } = useTranslation('job');
  const candidateCount = candidateIds.length;
  const isSingleCandidate = candidateCount === 1;
  const showNotificationToggle =
    isSingleCandidate && stageWillNotify && Boolean(onSendNotificationChange);
  const willNotify = stageWillNotify && (!showNotificationToggle || sendNotification);
  const singleCandidate = isSingleCandidate
    ? candidates.find((c) => c.id === candidateIds[0])
    : null;
  const isMissingEmailMode =
    isSingleCandidate && singleCandidate && !singleCandidate.hasValidEmail;
  const hasValidPendingEmail =
    isMissingEmailMode && isValidEmail(missingEmailValue.trim());
  const confirmDisabled = isPending || (isMissingEmailMode && !hasValidPendingEmail);

  const gtmConfig = { gtm: 'SEND_INTERVIEW' as const, origin };

  const recipientLabel = isSingleCandidate
    ? singleCandidate?.talentName?.trim() ||
      t('send_interview_dialog.candidate_fallback')
    : null;

  const bodyContent = (
    <div className="space-y-4">
      <div className="space-y-1">
        {recipientLabel ? (
          <CandidateIdentity
            name={recipientLabel}
            email={singleCandidate?.hasValidEmail ? singleCandidate.talentEmail : null}
          />
        ) : (
          <Typography variant="body-sm" color="primary" weight="semibold" as="p">
            {t('send_interview_dialog.recipients_bulk', { count: candidateCount })}
          </Typography>
        )}
        {!showNotificationToggle && (
          <Typography variant="body-sm" color="secondary" as="p">
            {t(
              willNotify
                ? 'send_interview_dialog.consequence_on'
                : 'send_interview_dialog.consequence_off'
            )}
          </Typography>
        )}
      </div>

      {showNotificationToggle && onSendNotificationChange && (
        <SendNotificationCheckbox
          id="send-interview-notification"
          label={t('send_interview_dialog.send_notification')}
          checked={sendNotification}
          onChange={onSendNotificationChange}
          disabled={isPending}
        />
      )}

      {isMissingEmailMode && (
        <Card
          variant="subtle"
          padding="sm"
          className="space-y-2"
          onClick={(e) => e.stopPropagation()}
        >
          <Typography variant="body-sm" color="primary" weight="semibold" as="p">
            {t('send_interview_dialog.missing_email_title')}
          </Typography>
          <Typography variant="body-sm" color="secondary" as="p">
            {t('send_interview_dialog.missing_email_message')}
          </Typography>
          {onMissingEmailChange && (
            <EmailField
              value={missingEmailValue}
              onChange={onMissingEmailChange}
              onSave={onConfirm}
              onCancel={onClose}
              showActions={false}
              placeholder={t('send_interview_dialog.email_placeholder')}
            />
          )}
        </Card>
      )}
    </div>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
      title={t('send_interview_dialog.title')}
    >
      <Dialog.Header
        titleVariant="h3"
        title={t('send_interview_dialog.title')}
        showCloseButton
        closeButtonProps={getGTMAttributes(
          getConfirmationDialogCloseTracking(gtmConfig)
        )}
      />
      <Dialog.Body className="space-y-4">{bodyContent}</Dialog.Body>
      <Dialog.Footer align="right">
        <Button
          {...getGTMAttributes(
            getConfirmationDialogButtonTracking(gtmConfig, {
              intent: 'secondary',
              isLastButton: false,
            })
          )}
          type="outlined"
          intent="secondary"
          onClick={onClose}
          disabled={isPending}
          label={t('send_interview_dialog.cancel')}
        />
        <Button
          {...getGTMAttributes(
            getConfirmationDialogButtonTracking(gtmConfig, {
              intent: 'secondary',
              isLastButton: true,
            })
          )}
          type="contained"
          intent="secondary"
          onClick={onConfirm}
          disabled={Boolean(confirmDisabled)}
          loading={isPending}
          label={
            isPending
              ? t('send_interview_dialog.sending')
              : t(
                  willNotify
                    ? 'send_interview_dialog.confirm'
                    : 'send_interview_dialog.confirm_without_email'
                )
          }
        />
      </Dialog.Footer>
    </Dialog>
  );
};
